import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, onAnimationEnd, isElementVisible } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element | null>>} */
  #pendingContent = new Map();
  /** @type {Map<string, Element[]>} */
  #cachedAwtomicRuntime = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#cartUpdateAbortController.signal.aborted) this.#cartUpdateAbortController = new AbortController();
    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#pendingContent.clear();
    this.#cachedAwtomicRuntime.clear();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#abortController?.abort();
    this.#cachedContent.clear();
    this.#pendingContent.clear();
    this.#cachedAwtomicRuntime.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      if (productGrid.querySelector('[data-bundleapp-wrapper]')) {
        await this.#ensureAwtomicRuntime(currentUrl);
      }

      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    // Search is a modal too. Complete its close/focus restoration before the
    // native options dialog opens; do not leave a hidden modal trapping focus.
    const searchDialog = this.closest('predictive-search-component')?.closest('dialog-component');
    if (searchDialog instanceof DialogComponent) await searchDialog.closeDialog();
    const headerDrawer = this.closest('header-drawer');
    if (headerDrawer?.isOpen) {
      headerDrawer.close();
      await new Promise((resolve) => onAnimationEnd(headerDrawer.querySelector('.menu-drawer'), resolve, { subtree: false }));
    }
    this.#openQuickAddModal();
  };

  /**
   * Warms quick-add content after sustained shopper intent.
   * @param {string} productPageUrl
   */
  prefetchProductPage(productPageUrl) {
    if (!productPageUrl || this.#cachedContent.has(productPageUrl) || this.#pendingContent.has(productPageUrl)) return;

    this.#getProductGrid(productPageUrl).catch(() => {
      // Prefetch failures are non-blocking; a click will retry normally.
    });
  }

  /**
   * Returns one cached product-grid source node per product URL.
   * @param {string} productPageUrl
   * @returns {Promise<Element | null>}
   */
  #getProductGrid(productPageUrl) {
    const cached = this.#cachedContent.get(productPageUrl);
    if (cached) return Promise.resolve(cached);

    const pending = this.#pendingContent.get(productPageUrl);
    if (pending) return pending;

    const request = this.fetchProductPage(productPageUrl)
      .then((html) => {
        const gridElement = html?.querySelector('[data-product-grid-content]');
        if (!gridElement) return null;

        const awtomicRuntime = Array.from(html.querySelectorAll('[data-bc-awtomic-runtime]'), (node) =>
          /** @type {Element} */ (node.cloneNode(true))
        );
        if (awtomicRuntime.length) {
          this.#cachedAwtomicRuntime.set(productPageUrl, awtomicRuntime);
        }

        const cachedGrid = /** @type {Element} */ (gridElement.cloneNode(true));
        this.#cachedContent.set(productPageUrl, cachedGrid);
        return cachedGrid;
      })
      .finally(() => {
        this.#pendingContent.delete(productPageUrl);
      });

    this.#pendingContent.set(productPageUrl, request);
    return request;
  }

  /**
   * Loads the subscription runtime only when quick-add opens a club product.
   * @param {string} productPageUrl
   */
  async #ensureAwtomicRuntime(productPageUrl) {
    if (window.bundleapp?.BundleAppWidget) return;

    if (window.__bcAwtomicRuntimePromise) {
      await window.__bcAwtomicRuntimePromise;
      return;
    }

    const runtimeNodes = this.#cachedAwtomicRuntime.get(productPageUrl);
    if (!runtimeNodes?.length) return;

    window.__bcAwtomicRuntimePromise = Promise.resolve().then(() => {
      for (const sourceNode of runtimeNodes) {
        if (sourceNode instanceof HTMLStyleElement) {
          const style = document.createElement('style');
          style.dataset.bcAwtomicActive = '';
          style.textContent = sourceNode.textContent;
          document.head.appendChild(style);
          continue;
        }

        if (sourceNode instanceof HTMLLinkElement) {
          const link = document.createElement('link');
          for (const { name, value } of sourceNode.attributes) {
            if (name !== 'data-bc-awtomic-runtime') link.setAttribute(name, value);
          }
          link.dataset.bcAwtomicActive = '';
          document.head.appendChild(link);
          continue;
        }

        if (sourceNode instanceof HTMLScriptElement) {
          const script = document.createElement('script');
          for (const { name, value } of sourceNode.attributes) {
            if (name !== 'data-bc-awtomic-runtime') script.setAttribute(name, value);
          }
          script.dataset.bcAwtomicActive = '';
          script.textContent = sourceNode.textContent;
          document.body.appendChild(script);
        }
      }
    });

    await window.__bcAwtomicRuntimePromise;
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #ensureQuickAddDialog() {
    let dialogComponent = document.getElementById('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) return dialogComponent;

    const template = document.getElementById('quick-add-dialog-template');
    if (template instanceof HTMLTemplateElement) {
      document.body.appendChild(template.content.cloneNode(true));
      dialogComponent = document.getElementById('quick-add-dialog');
    }

    return dialogComponent instanceof QuickAddDialog ? dialogComponent : null;
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  #openQuickAddModal = () => {
    const dialogComponent = this.#ensureQuickAddDialog();
    if (!dialogComponent) return;

    // Keep cart-origin options inside the drawer's native nested-dialog lifecycle.
    // Its Escape and focus handling already supports nested modal dialogs.
    const cartPanel = this.closest('theme-drawer[open]')?.querySelector(':scope > dialog');
    const dialogHost = cartPanel || document.body;
    if (dialogComponent.parentElement !== dialogHost) dialogHost.append(dialogComponent);
    dialogComponent.setCartFocusContext(cartPanel || null, document.activeElement instanceof HTMLElement ? document.activeElement : null);

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#resetScroll();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    try {
      const response = await fetch(productPageUrl, {
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      if (this.#abortController === abortController) {
        this.#abortController = null;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    this.#ensureQuickAddDialog();
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productFormPrice = productFormComponent?.querySelector('.bc-buyrow__price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      // Some product templates already render the price inside the buy-button
      // row. Do not append that node to itself, which throws on mobile before
      // the quick-add dialog can open.
      if (productPrice && !productFormPrice?.contains(productPrice)) {
        if (productFormPrice && !productPrice.contains(productFormPrice)) {
          productFormPrice.appendChild(productPrice);
        } else {
          productHeader.appendChild(productPrice);
        }
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#initializeAwtomicWidgets(modalContent);
  }

  /**
   * Initializes subscription selectors because scripts from fetched HTML do
   * not execute when the quick-add DOM is morphed into place.
   * @param {Element} modalContent
   */
  #initializeAwtomicWidgets(modalContent) {
    if (!window.bundleapp?.BundleAppWidget) return;

    const wrappers = modalContent.querySelectorAll('[data-bundleapp-wrapper]');
    for (const wrapper of wrappers) {
      if (!(wrapper instanceof HTMLElement) || wrapper.dataset.bcAwtomicInitialized === 'true') continue;

      const productData = wrapper.parentElement?.nextElementSibling;
      if (!(productData instanceof HTMLScriptElement) || productData.type !== 'application/json') continue;

      try {
        const product = JSON.parse(productData.textContent || '');
        new window.bundleapp.BundleAppWidget(wrapper, product);
        wrapper.dataset.bcAwtomicInitialized = 'true';
      } catch (error) {
        console.error('Failed to initialize the subscription selector', error);
      }
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {HTMLDialogElement | null} */
  #cartPanel = null;
  /** @type {HTMLElement | null} */
  #cartTrigger = null;

  /** @param {HTMLDialogElement | null} panel @param {HTMLElement | null} trigger */
  setCartFocusContext(panel, trigger) {
    this.#cartPanel = panel;
    this.#cartTrigger = trigger;
  }

  #handleDialogOpen = () => {
    if (!this.#cartPanel) return;
    const initial = document.activeElement;
    trapFocus(this.refs.dialog);
    if (initial instanceof HTMLElement && this.refs.dialog.contains(initial)) initial.focus({ preventScroll: true });
  };

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    // Restore the owning cart's trap after native modal close and after any cart morph.
    if (this.#cartPanel) {
      removeTrapFocus();
      if (this.#cartPanel.isConnected && this.#cartPanel.open) {
        trapFocus(this.#cartPanel);
        const trigger = this.#cartTrigger?.isConnected && isElementVisible(this.#cartTrigger, { visibilityProperty: true })
          ? this.#cartTrigger : this.#cartPanel.querySelector('.theme-drawer__close-button');
        if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
      }
      this.#cartPanel = null;
      this.#cartTrigger = null;
    }
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
